import ariaLiveRegionContext, {
  ARIA_LIVE_REGION_EVENTS,
} from '@theme/context-aria-live-region'
import cartContext from '@theme/context-cart'
import cartSectionsContext from '@theme/context-cart-sections'
import deferredMediaContext from '@theme/context-deferred-media'
import modalContext from '@theme/context-modal'
import quickShopContext from '@theme/context-quickshop'
import { provide, LitElement } from '@theme/vendor-lit'

const DIALOG_EVENTS = {
  OPEN: 'modal-dialog::open',
  CLOSE: 'modal-dialog::close',
  CLOSE_ALL: 'modal-dialog::close-all',
}

const CART_EVENTS = {
  UPDATED: 'cart:updated',
  UPDATED_SECTIONS: 'cart:updated-sections',
}

const DEFERRED_MEDIA_EVENTS = {
  PAUSE: 'deferred-media::pause',
  PLAY: 'deferred-media::play',
}

const QUICKSHOP_EVENTS = {
  OPEN: 'quickshop::open',
  CLOSE: 'quickshop::close',
}

const MODAL_PARAM_KEY = 'modals'
export class AppProvider extends LitElement {
  constructor() {
    super(...arguments)

    this.cart = window.__CART__
    this.cartSections = {}
    this.modals = []
    this.activeDeferredMedia = null
    this.quickShop = null
  }
  static get properties() {
    return {
      cart: { state: true },
      cartSections: { state: true },
      modals: { state: true },
      ariaLiveRegion: { state: true },
      activeDeferredMedia: { state: true },
      quickShop: { state: true },
    }
  }

  createRenderRoot() {
    return this
  }

  connectedCallback() {
    this.addEventListener(
      CART_EVENTS.UPDATED,
      this._handleCartUpdate.bind(this)
    )

    this.addEventListener(
      CART_EVENTS.UPDATED_SECTIONS,
      this._handleCartSectionsUpdate.bind(this)
    )

    this.addEventListener(
      ARIA_LIVE_REGION_EVENTS.SET,
      this._handleAriaLiveRegionSet.bind(this)
    )

    this.addEventListener(
      ARIA_LIVE_REGION_EVENTS.CLEAR,
      this._handleAriaLiveRegionClear.bind(this)
    )

    this.addEventListener(
      DEFERRED_MEDIA_EVENTS.PAUSE,
      this._handleDeferredMediaPause.bind(this)
    )

    this.addEventListener(
      DEFERRED_MEDIA_EVENTS.PLAY,
      this._handleDeferredMediaPlay.bind(this)
    )

    /*
     * Custom schema watcher for modal-dialog:// links and open the relevant dialog
     */
    this.addEventListener('click', this._handleDialogSchemaClick.bind(this))

    Object.values(DIALOG_EVENTS).forEach((event) => {
      this.addEventListener(event, this._handleDialogEvent.bind(this))
    })

    Object.values(QUICKSHOP_EVENTS).forEach((event) => {
      this.addEventListener(event, this._handleQuickShopEvent.bind(this))
    })

    this._handleRestoreModals()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener(
      CART_EVENTS.UPDATED,
      this._handleCartUpdate.bind(this)
    )

    this.removeEventListener(
      CART_EVENTS.UPDATED_SECTIONS,
      this._handleCartSectionsUpdate.bind(this)
    )

    this.removeEventListener(
      ARIA_LIVE_REGION_EVENTS.SET,
      this._handleAriaLiveRegionSet.bind(this)
    )

    this.removeEventListener(
      ARIA_LIVE_REGION_EVENTS.CLEAR,
      this._handleAriaLiveRegionClear.bind(this)
    )

    this.removeEventListener(
      DEFERRED_MEDIA_EVENTS.PAUSE,
      this._handleDeferredMediaPause.bind(this)
    )

    this.removeEventListener(
      DEFERRED_MEDIA_EVENTS.PLAY,
      this._handleDeferredMediaPlay.bind(this)
    )

    this.removeEventListener('click', this._handleDialogSchemaClick.bind(this))

    Object.values(DIALOG_EVENTS).forEach((event) => {
      this.removeEventListener(event, this._handleDialogEvent.bind(this))
    })

    Object.values(QUICKSHOP_EVENTS).forEach((event) => {
      this.removeEventListener(event, this._handleQuickShopEvent.bind(this))
    })
  }

  _handleDeferredMediaPlay(event) {
    const composedPath = event.composedPath()
    const target = composedPath.length > 0 ? composedPath[0] : event.target

    if (
      target === this.activeDeferredMedia ||
      target.tagName !== 'DEFERRED-MEDIA'
    ) {
      return
    }
    this.activeDeferredMedia = target
  }

  _handleDeferredMediaPause() {
    this._pauseAllDeferredMedia()
  }

  _handleCartUpdate(event) {
    if (event.detail) {
      this.cart = event.detail
    }
  }

  _handleCartSectionsUpdate(event) {
    if (event.detail) {
      this.cartSections = event.detail || {}
    }
  }

  _handleDialogSchemaClick(event) {
    const composedPath = event.composedPath()
    const target = composedPath.length > 0 ? composedPath[0] : event.target

    if (target.tagName === 'A') {
      const href = target.getAttribute('href')

      // Check if the href starts with the custom scheme
      // it can start with modal-dialog:// or #modal-dialog://
      if (
        href &&
        (href.startsWith('modal-dialog://') ||
          href.startsWith('#modal-dialog://'))
      ) {
        event.preventDefault() // Prevent the default navigation
        // Extract the dialog ID
        const dialogId = href.replace('modal-dialog://', '').replace('#', '')
        // Execute the custom function
        !!dialogId &&
          this._handleDialogEvent(
            new CustomEvent(DIALOG_EVENTS.OPEN, { detail: dialogId })
          )
      }
    }
  }

  _handleDialogEvent(event) {
    let modals = [...this.modals]
    switch (event.type) {
      case DIALOG_EVENTS.OPEN:
        if (event.detail) {
          modals = [...modals, event.detail]
        }
        break

      case DIALOG_EVENTS.CLOSE:
        if (event.detail) {
          modals = modals.filter((modal) => modal !== event.detail)
        }
        this.quickShop = null
        break

      case DIALOG_EVENTS.CLOSE_ALL:
        modals = []
        this.quickShop = null
        break
    }

    modals = [...new Set(modals)]
    this.modals = modals
    this._applyModalsState()
    this._pauseAllDeferredMedia()
    this._updateQueryParams()
  }

  _pauseAllDeferredMedia() {
    try {
      if (this.activeDeferredMedia === null) {
        const allDeferredMedia = Array.from(
          this.querySelectorAll('deferred-media')
        )
        allDeferredMedia.forEach((media) => media.pause(true))
        return
      }
      this.activeDeferredMedia = null
    } catch (error) {
      console.error(error)
    }
  }

  async _applyModalsState() {
    await customElements.whenDefined('modal-dialog')
    const modals = this.modals
    const allModalDialogs = Array.from(this.querySelectorAll('modal-dialog'))

    for (const dialog of allModalDialogs) {
      if (modals.includes(dialog.id)) {
        dialog.show()
      } else {
        dialog.hide()
      }
    }
  }

  _handleRestoreModals() {
    const params = new URLSearchParams(window.location.search)
    const modals = params.get(MODAL_PARAM_KEY)
    if (modals) {
      this.modals = modals
        .split(',')
        .map((modal) => modal.trim())
        .filter(Boolean)
      this._applyModalsState()
    }
  }

  _updateQueryParams() {
    const params = new URLSearchParams(window.location.search)
    const modals = this.modals
    const modalIds = this.modals.join(',')

    if (modals.length > 0) {
      params.set(MODAL_PARAM_KEY, modalIds)
    } else {
      params.delete(MODAL_PARAM_KEY)
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState({}, '', newUrl)
  }

  _handleAriaLiveRegionSet(event) {
    if (event.detail) {
      this.ariaLiveRegion = event.detail
    }
  }

  _handleAriaLiveRegionClear() {
    this.ariaLiveRegion = {
      message: '',
    }
  }

  _handleQuickShopEvent(event) {
    const type = event.type
    const detail = event.detail
    if (type === QUICKSHOP_EVENTS.OPEN) {
      this.quickShop = detail
    } else {
      this.quickShop = null
    }
  }
}

// Share these values with descendant components (Lit context)
provide({ context: cartContext })(AppProvider.prototype, 'cart')
provide({ context: cartSectionsContext })(AppProvider.prototype, 'cartSections')
provide({ context: modalContext })(AppProvider.prototype, 'modals')
provide({ context: ariaLiveRegionContext })(
  AppProvider.prototype,
  'ariaLiveRegion'
)
provide({ context: deferredMediaContext })(
  AppProvider.prototype,
  'activeDeferredMedia'
)
provide({ context: quickShopContext })(AppProvider.prototype, 'quickShop')
customElements.define('app-provider', AppProvider)
